import { area, feature, length } from "@turf/turf";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { measurementString } from "react-migration/lib/util/conversion";
import { LayerTypeSelectionPrintableSummaryProps } from "../../types";
import { useSiteById } from "react-migration/domains/sites/hooks/useSiteById";
import { useSnapshot } from "valtio";
import { MeasurementSystem, userStore } from "src/js/stores/user/store";
import hasFeature from "src/js/stores/user/actions/hasFeature";
import Feature from "src/js/stores/user/Feature";
import { StaticMapContainer } from "react-migration/domains/sites/card/Map/StaticMapContainer";
import { useEffect, useReducer } from "react";
import { SelectionType } from "src/js/stores/map/store";

const SUMMARY_MAPS = ["roadmap", "satellite"];

export function SitesSelectionReportSummary({ selection, onLoaded }: LayerTypeSelectionPrintableSummaryProps) {
  const isShared = selection.type === SelectionType.SHARED_SITE;
  const { site, loading } = useSiteById(selection.id, isShared);
  const { t } = useTranslation();
  const { unit } = useSnapshot(userStore.user.settings);

  const isImperial = unit === MeasurementSystem.IMPERIAL;

  const [mapLoaded, setMapLoaded] = useReducer(() => true, false);

  useEffect(() => {
    if (!loading && mapLoaded) {
      onLoaded();
    }
  }, [loading, mapLoaded, onLoaded]);

  if (!site) return null;

  return (
    <div>
      <h1 className="text-xl font-semibold text-text-base-primary" title={site?.title}>
        {site?.title}
      </h1>
      <div className="flex mt-4 mb-4">
        <div className="flex grow min-w-1/3">
          <div className="mb-4">
            <h3>{t("sites.card.site_report.site_information")}</h3>
            {site.geometry && (
              <>
                <div>
                  {t("sites.card.site_report.area")}: {measurementString(area(site.geometry), "hectares", isImperial)}
                </div>
                <div>
                  {t("sites.card.site_report.perimeter")}:{" "}
                  {measurementString(1000 * length(feature(site.geometry)), "m", isImperial)}
                </div>
              </>
            )}
            {hasFeature(Feature.propertyInformationLayer) && (
              <>
                <div>
                  {t("sites.card.site_report.authority")}: {site.local_authority}
                </div>
                <div>
                  {t("sites.card.site_report.ptal")}: {site.ptal_rating ?? "None"}
                </div>
              </>
            )}
          </div>
        </div>
        <div className="max-w-2/3 flex [&>div]:mr-4 [&>div:last-child]:mr-0">
          {SUMMARY_MAPS.map((mapType) => (
            <div key={mapType} className="w-1/2 aspect-square">
              <StaticMapContainer
                siteId={site._id}
                isShared={isShared}
                mapId={`site-${mapType}`}
                initialMapTypeId={mapType}
                mapDimensions={{ height: 230, width: 230 }}
                onMapLoaded={setMapLoaded}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
